import { useEffect, useState } from 'react'
import {
  AvailableBalanceCard,
  CurrentMonthlyVolumesCard,
  FilterStatus,
  FlaggedTransactionsCard,
  PendingTransactionsCard,
  SearchInput,
  StatsWrapper,
  Tabs,
  TabsContainer,
  TabsContent,
  TabsList,
  TabsTrigger,
  useInterval,
} from '@valiant/ui-shared'
import { GroupsTab } from '../components/tabs/GroupsTab'
import { CompaniesTab } from '../components/tabs/CompaniesTab'
import { UsersTab } from '../components/tabs/UsersTab'
import { TransactionsTab } from '../components/tabs/TransactionsTab'
import { GasTab } from '../components/tabs/GasTab'
import { VolumesTab } from '../components/tabs/VolumesTab'
import { useGetDashboardStatsQuery } from '@/generated/graphql'
import { useAuth } from '@/AuthProvider'
import { useQueryParams } from '@valiant/ui-shared'
import { MainLayout } from '@/components/layout/MainLayout'

export function Dashboard() {
  const { auth } = useAuth()

  const [searchTerm, setSearchTerm] = useState('')
  const isComplianceUser = auth?.role === 'COMPLIANCE'
  const defaultTab = isComplianceUser ? 'companies' : 'groups'
  const queryParams = useQueryParams(defaultTab)
  const [{ data: statsData, fetching: statsFetching }, refetchStats] = useGetDashboardStatsQuery()

  // Auto-refetch dashboard stats every 30 seconds
  useInterval(() => {
    refetchStats({ requestPolicy: 'network-only' })
  }, 30000)

  const stats = statsData?.getDashboardStats || {
    activeCompanies: '0',
    availableBalance: '$0.00',
    groups: '0',
    flaggedTransactions: '0',
    currentMonthlyVolumes: '$0',
    previousMonthlyVolumes: '$0',
    awaitingApprovalSwaps: '0',
    awaitingApprovalDeposits: '0',
    pendingSwaps: '0',
    pendingDeposits: '0',
  }

  useEffect(() => {
    setSearchTerm('')
  }, [queryParams.tab])

  return (
    <MainLayout hideBack={true}>
      {/* Stats Cards */}
      <StatsWrapper>
        <AvailableBalanceCard
          availableBalance={stats.availableBalance}
          loading={statsFetching}
          onClick={() => {
            queryParams.updateMultiple({
              tab: 'companies',
              status: undefined,
              hasFunds: 'true',
            })
          }}
        />
        <PendingTransactionsCard
          pendingTransactions={
            Number(stats.awaitingApprovalSwaps || 0) + Number(stats.awaitingApprovalDeposits || 0)
          }
          onClick={() => {
            queryParams.updateMultiple({
              tab: 'transactions',
              status: FilterStatus.Awaiting,
              type: undefined,
              isFlagged: undefined,
            })
          }}
        />
        <FlaggedTransactionsCard
          flaggedTransactions={Number(stats.flaggedTransactions)}
          onClick={() => {
            queryParams.updateMultiple({
              tab: 'transactions',
              isFlagged: 'true',
              status: undefined,
              type: undefined,
            })
          }}
        />
        <CurrentMonthlyVolumesCard
          currentMonthlyVolumes={stats.currentMonthlyVolumes}
          previousMonthlyVolumes={stats.previousMonthlyVolumes}
          onClick={() => {
            queryParams.updateMultiple({ tab: 'volumes', status: undefined })
          }}
        />
      </StatsWrapper>

      {/* Tabs Section */}
      <Tabs
        value={queryParams.tab || defaultTab}
        onValueChange={(value) => {
          // Clear transactions filter when switching to other tabs
          if (value !== 'transactions') {
            queryParams.updateMultiple({
              tab: value,
              status: undefined,
              type: undefined,
              isFlagged: undefined,
              hasFunds: value === 'companies' ? queryParams.hasFunds : undefined,
            })
          } else {
            queryParams.updateTab(value)
          }
        }}
        className="space-y-4"
      >
        <TabsContainer>
          <TabsList>
            {!isComplianceUser && <TabsTrigger value="groups">Groups</TabsTrigger>}
            <TabsTrigger value="companies">Companies</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="gas">Gas</TabsTrigger>
            <TabsTrigger value="volumes">Volumes</TabsTrigger>
          </TabsList>

          <SearchInput
            placeholder="Search"
            className="w-[200px]"
            value={searchTerm}
            onChange={setSearchTerm}
          />
        </TabsContainer>

        {!isComplianceUser && (
          <TabsContent value="groups" className="space-y-4">
            <GroupsTab searchTerm={searchTerm} />
          </TabsContent>
        )}

        <TabsContent value="companies">
          <CompaniesTab searchTerm={searchTerm} />
        </TabsContent>

        <TabsContent value="users">
          <UsersTab searchTerm={searchTerm} />
        </TabsContent>

        <TabsContent value="transactions">
          <TransactionsTab searchTerm={searchTerm} />
        </TabsContent>

        <TabsContent value="gas">
          <GasTab />
        </TabsContent>

        <TabsContent value="volumes">
          <VolumesTab searchTerm={searchTerm} />
        </TabsContent>
      </Tabs>
    </MainLayout>
  )
}
